import React, { useState, useContext, useEffect } from "react";
import { Container, Row, Col, Card, Form, Button } from "react-bootstrap";
import { IoSend } from "react-icons/io5";
import ApiService from "../../ApiService";
import { SnackbarContext } from "../AlertSystem";
import Select from "react-select";

const RESULT_TYPES = [
  { label: "PrEP", value: "PREP" },
  { label: "STI", value: "STI" },
  { label: "PrEP Maintenance", value: "PREP_MAINTENANCE" }
];

const RESULTS_CODES = {
  444425: { name: "Bacterial Vaginosis" },
  4302: { name: "Chlamydia" },
  4303: { name: "Gonorrhea" },
  4408: { name: "Hepatitis B" },
  4406: { name: "Hepatitis C" },
  8545: { name: "Herpes" },
  4415: { name: "HIV" },
  444403: { name: "HPV" },
  4243: { name: "Mycoplasma Genitalium" },
  4446: { name: "Syphilis" },
  817: { name: "Trichomoniasis" }
};

const DISEASE_OPTIONS = Object.values(
  Object.keys(RESULTS_CODES).reduce((acc, code) => {
    const { name } = RESULTS_CODES[code];
    if (!acc[name]) acc[name] = { code, name };
    return acc;
  }, {})
).map(({ code, name }) => ({
  label: name,
  value: code
}));

const ALLOWED_RESULT_FILE_TYPES = ["application/pdf"];

const UploadResults = () => {
  const { showSnackbar } = useContext(SnackbarContext);
  const [email, setEmail] = useState("");
  const [orders, setOrders] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [resultType, setResultType] = useState("");
  const [selectedDiseases, setSelectedDiseases] = useState([]);
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingOrders, setIsFetchingOrders] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const patientId = new URLSearchParams(window.location.search).get("patientId");
  const [patientDetail, setPatientDetail] = useState({});

  const getOrders = async emailId => {
    setOrders([]);
    setIsFetchingOrders(true);
    if (!emailId) {
      showSnackbar({ message: "Email is required to fetch orders", severity: "warning" });
      setIsFetchingOrders(false);
      return;
    }
    try {
      const response = await ApiService.getPatientOrders(emailId);
      if (response?.data?.length) {
        const labcorpOrders = response.data.filter(order => order.vendor === "LABCORP");
        if (!labcorpOrders.length) {
          showSnackbar({ message: "No Labcorp orders found for this patient", severity: "info" });
        }
        setOrders(labcorpOrders);
      } else {
        showSnackbar({ message: "No orders found for this email", severity: "info" });
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      showSnackbar({ message: "Failed to fetch orders", severity: "error" });
    } finally {
      setIsFetchingOrders(false);
    }
  };

  const fetchPatientDetails = async () => {
    if (!patientId) {
      showSnackbar({ message: "Patient ID is required to fetch details", severity: "error" });
      return;
    }
    setIsLoading(true);
    setIsError(false);
    try {
      const res = await ApiService.getPatientDetailsBySubId(patientId);
      if (res?.data) {
        const data = res.data;
        setPatientDetail(data);
        if (data.email) {
          setEmail(data.email);
          await getOrders(data.email);
        } else {
          showSnackbar({ message: "No email found for this patient", severity: "warning" });
        }
      }
    } catch (error) {
      console.error("Error fetching patient details:", error);
      setIsError(true);
      showSnackbar({ message: "Error fetching patient details", severity: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (patientId && !patientDetail?.email && !isLoading && !isError) {
      fetchPatientDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const convertFileToBase64 = fileToConvert =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result !== "string") {
          reject(new Error("Failed to read uploaded file"));
          return;
        }
        const [, base64String] = reader.result.split(",");
        if (!base64String) {
          reject(new Error("Unable to convert file to base64"));
          return;
        }
        resolve(base64String);
      };
      reader.onerror = () => reject(new Error("Failed to read uploaded file"));
      reader.readAsDataURL(fileToConvert);
    });

  const handleFileChange = event => {
    const selectedFile = event?.target?.files?.[0];
    if (!selectedFile) return;

    if (!ALLOWED_RESULT_FILE_TYPES.includes(selectedFile.type)) {
      showSnackbar({ message: "Only PDF result files are supported", severity: "warning" });
      event.target.value = "";
      setFile(null);
      return;
    }

    setFile(selectedFile);
  };

  const handleSubmit = async () => {
    if (!selectedOrderId) {
      showSnackbar({ message: "Please select an order ID", severity: "warning" });
      return;
    }
    if (!resultType) {
      showSnackbar({ message: "Please specify the kit or result type", severity: "warning" });
      return;
    }
    if (!file) {
      showSnackbar({ message: "Please upload a file", severity: "warning" });
      return;
    }
    if (!ALLOWED_RESULT_FILE_TYPES.includes(file.type)) {
      showSnackbar({ message: "Only PDF result files are supported", severity: "warning" });
      return;
    }

    try {
      setIsSubmitting(true);
      const base64File = await convertFileToBase64(file);
      const payload = {
        orderId: selectedOrderId,
        resultPdf: base64File,
        positiveTests: selectedDiseases.map(disease => disease.value),
        type: resultType
      };

      await ApiService.uploadLabcorpResultsV1(payload);
      showSnackbar({ message: "Results uploaded successfully", severity: "success" });

      setFile(null);
      setSelectedOrderId("");
      setResultType("");
      setSelectedDiseases([]);
    } catch (error) {
      console.error("Error uploading results:", error);
      showSnackbar({ message: "Failed to upload results", severity: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    background: "#f9f9f9",
    border: "1px solid #ccc",
    padding: "14px 16px",
    borderRadius: "8px",
    width: "100%",
    marginTop: "4px",
    height: "48px"
  };

  const labelStyle = {
    fontWeight: "600",
    fontSize: "1rem",
    marginBottom: "12px",
    textTransform: "uppercase"
  };

  return (
    <Container fluid>
      <Row className="justify-content-center mt-4">
        <Col md={8} lg={6}>
          <div style={{ textAlign: "center", fontSize: "24px", fontWeight: "600", marginBottom: "24px" }}>Upload Results</div>
          <Card style={{ padding: "36px 30px", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <Form>
              <Form.Group controlId="email" style={{ marginBottom: "24px" }}>
                <Form.Label style={labelStyle}>Patient Email</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Enter patient's email"
                  style={inputStyle}
                />
              </Form.Group>
              {orders.length === 0 && (
                <Button
                  variant="primary"
                  onClick={() => getOrders(email)}
                  style={{ marginTop: "12px", width: "100%", padding: "14px" }}
                  disabled={isFetchingOrders}
                >
                  {isFetchingOrders ? "Fetching Orders..." : "Fetch Orders"}
                </Button>
              )}

              {orders.length > 0 && (
                <>
                  <Form.Group controlId="orderId" style={{ marginBottom: "24px" }}>
                    <Form.Label style={labelStyle}>Select Order ID</Form.Label>
                    <Form.Select value={selectedOrderId} onChange={e => setSelectedOrderId(e.target.value)} style={inputStyle}>
                      <option value="">Select order</option>
                      {orders.map(order => (
                        <option key={order.id} value={order.id}>
                          {order.id} - {order?.line_item?.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group controlId="resultType" style={{ marginBottom: "24px" }}>
                    <Form.Label style={labelStyle}>Result Type</Form.Label>
                    <Form.Select value={resultType} onChange={e => setResultType(e.target.value)} style={inputStyle}>
                      <option value="">Select type</option>
                      {RESULT_TYPES.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group controlId="positiveTests" style={{ marginBottom: "24px" }}>
                    <Form.Label style={labelStyle}>Positive Tests (optional)</Form.Label>
                    <Select
                      isMulti
                      options={DISEASE_OPTIONS}
                      value={selectedDiseases}
                      onChange={setSelectedDiseases}
                      placeholder="Select positive STIs..."
                      className="basic-multi-select"
                      classNamePrefix="select"
                    />
                  </Form.Group>

                  <Form.Group controlId="fileUpload" style={{ marginBottom: "24px" }}>
                    <Form.Label style={labelStyle}>Upload Result File</Form.Label>
                    <div className="custom-file-upload-wrapper">
                      <label
                        htmlFor="file-upload"
                        className="custom-file-upload"
                        style={{
                          cursor: "pointer",
                          display: "inline-block",
                          padding: "12px 16px",
                          backgroundColor: "#f9f9f9",
                          border: "1px solid #ccc",
                          borderRadius: "8px",
                          width: "100%",
                          textAlign: "center",
                          height: "48px",
                          lineHeight: "24px",
                          color: "#333"
                        }}
                      >
                        {file ? file.name : "Choose File"}
                      </label>
                      <input
                        id="file-upload"
                        type="file"
                        accept="application/pdf,.pdf"
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                      />
                    </div>
                  </Form.Group>

                  <Button
                    onClick={handleSubmit}
                    style={{ marginTop: "12px", width: "100%", padding: "14px", background: "#007F61", border: "none" }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>Submitting...</>
                    ) : (
                      <>
                        <IoSend style={{ marginRight: "8px" }} />
                        Submit Result
                      </>
                    )}
                  </Button>
                </>
              )}
            </Form>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default UploadResults;
